import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import RepeatIcon from "@material-ui/icons/Repeat";
import DateRangeIcon from "@material-ui/icons/DateRange";
import EventIcon from "@material-ui/icons/Event";
import { useSnackbar } from "notistack";
import { Get, Post } from "../../../../actions";
import {
    // TODO: Add to apiUrls constants
    // Delete_BlockRule_URL → POST /api/block-services/delete
    Delete_BlockRule_URL,
} from "../../../../constants/apiUrls";
import { getTranslation } from "../../../../heplers/translationHelper";
import AlertDialog from "../../../../components/DeleteDialog";
import { daysConstant } from "../../../../constants/general";

// ─── constants ───────────────────────────────────────────────────────────────
const DAY_TIME_LABELS = {
    0: { label: "Morning",   color: "#fff8e1", text: "#f57f17" },
    1: { label: "Evening",   color: "#ede7f6", text: "#4527a0" },
    2: { label: "Whole Day", color: "#e8f5e9", text: "#2e7d32" },
};

const BLOCK_TYPE_CONFIG = {
    recurring: { label: "Recurring", icon: RepeatIcon,   color: "#e3f2fd", text: "#1565c0" },
    period:    { label: "Period",    icon: DateRangeIcon, color: "#fff3e0", text: "#e65100" },
    date:      { label: "Date",      icon: EventIcon,     color: "#fce4ec", text: "#880e4f" },
};

const DAY_LABELS = ["Sun","Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_OPTIONS = [
    { id: "0", label: "Sun" },
    { id: "1", label: "Mon" },
    { id: "2", label: "Tue" },
    { id: "3", label: "Wed" },
    { id: "4", label: "Thu" },
    { id: "5", label: "Fri" },
    { id: "6", label: "Sat" },
    
];

// ─── styles ──────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
    root: {
        marginTop: theme.spacing(1),
    },
    toolbar: {
        display: "flex",
        justifyContent: "flex-end",
        marginBottom: theme.spacing(1.5),
    },
    addButton: {
        backgroundColor: "#0d4f6c",
        color: "#fff",
        fontWeight: 600,
        "&:hover": { backgroundColor: "#0a3d55" },
    },
    tableContainer: {
        borderRadius: 10,
        boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
        border: "1px solid #eceff1",
    },
    tableHead: {
        backgroundColor: "#0d4f6c",
        "& th": {
            color: "#fff",
            fontWeight: 600,
            fontSize: 13,
            padding: "12px 16px",
            whiteSpace: "nowrap",
        },
    },
    tableRow: {
        "&:hover": { backgroundColor: "#f5f9fb" },
        transition: "background 0.15s",
    },
    tableCell: {
        padding: "10px 16px",
        verticalAlign: "middle",
    },
    typeBadge: {
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "4px 10px",
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 600,
    },
    dayTimeChip: {
        fontSize: 11,
        fontWeight: 600,
        height: 22,
    },
    roomChip: {
        fontSize: 11,
        margin: "2px",
        height: 22,
        backgroundColor: "#e8f4fd",
        color: "#0d4f6c",
        fontWeight: 500,
    },
    dayChip: {
        fontSize: 11,
        margin: "2px",
        height: 22,
        backgroundColor: "#f3e5f5",
        color: "#6a1b9a",
        fontWeight: 500,
    },
    whenText: {
        fontSize: 13,
        color: "#37474f",
        fontWeight: 500,
    },
    reasonText: {
        fontSize: 12,
        color: "#90a4ae",
        fontStyle: "italic",
        marginTop: 2,
    },
    emptyState: {
        textAlign: "center",
        padding: theme.spacing(6),
        color: "#90a4ae",
    },
    emptyIcon: {
        fontSize: 48,
        color: "#cfd8dc",
        marginBottom: theme.spacing(1),
    },
    deleteBtn: { color: "#e53935" },
    editBtn:   { color: "#0d4f6c" },
    loading: {
        display: "flex",
        justifyContent: "center",
        padding: theme.spacing(4),
    },
}));

// ─── helpers ─────────────────────────────────────────────────────────────────
const formatWhen = (rule) => {
    if (rule.blockType === "recurring") {
       const dayLabels = (rule.days || []).sort((a, b) => Number(a) - Number(b)).map((d) => (DAY_LABELS[d] ? getTranslation(DAY_LABELS[d]) : "")).join(", ")
        // const dayLabels = (rule.days || [])
        //     .map((d) => DAY_LABELS[d] || "")
        //     .filter(Boolean)
        //     .join(", ");
        return `${getTranslation("Every")} ${dayLabels}`;
    }
    if (rule.blockType === "period") {
        return `${rule.fromDate} → ${rule.toDate}`;
    }
    if (rule.blockType === "date") {
        return rule.specificDate;
    }
    return "—";
};

// ─── component ───────────────────────────────────────────────────────────────
const BlockRulesTable = ({ blockRules, loading, rooms, onAdd, onEdit, onDeleted }) => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDeleteConfirm = () => {
        setIsDeleting(true);
        Post(
    { id: deleteTarget.id },
    Delete_BlockRule_URL,
    null,
    (res) => {
        enqueueSnackbar(
            getTranslation("Block rule deleted", "Block rule deleted", "Block rule deleted"),
            { variant: "success" }
        );
        setDeleteTarget(null);
        setIsDeleting(false);
        onDeleted();
    },
    (err) => {
        enqueueSnackbar(
            err?.data || getTranslation("Something went wrong", "Something went wrong", "Something went wrong"),
            { variant: "error" }
        );
        setIsDeleting(false);
    }
);
        /**
         * TODO — API: POST /api/block-services/delete
         * Body: { id: deleteTarget.id }
         * On success: onDeleted()
         *
         * Post(
         *   { id: deleteTarget.id },
         *   Delete_BlockRule_URL,
         *   null,
         *   (res) => {
         *     enqueueSnackbar("Block rule deleted", { variant: "success" });
         *     setDeleteTarget(null);
         *     setIsDeleting(false);
         *     onDeleted();
         *   },
         *   (err) => {
         *     enqueueSnackbar(err?.data || "Something went wrong", { variant: "error" });
         *     setIsDeleting(false);
         *   }
         * );
         *
         * Remove mock below:
         */
        // setTimeout(() => {
        //     enqueueSnackbar(
        //         getTranslation("Block rule deleted", "Block rule deleted", "Block rule deleted"),
        //         { variant: "success" }
        //     );
        //     setDeleteTarget(null);
        //     setIsDeleting(false);
        //     onDeleted();
        // }, 500);
    };

    return (
        <Box className={classes.root}>
            {/* Toolbar */}
            <Box className={classes.toolbar}>
                <Button
                    variant="contained"
                    className={classes.addButton}
                    startIcon={<AddIcon />}
                    onClick={onAdd}
                >
                    {getTranslation("Add Block Rule", "Add Block Rule", "Add Block Rule")}
                </Button>
            </Box>

            {/* Table */}
            <TableContainer component={Paper} className={classes.tableContainer}>
                <Table size="small">
                    <TableHead className={classes.tableHead}>
                        <TableRow>
                            <TableCell>{getTranslation("Type",     "Type",     "Type")}</TableCell>
                            <TableCell>{getTranslation("When",     "When",     "When")}</TableCell>
                            <TableCell>{getTranslation("Day Time", "Day Time", "Day Time")}</TableCell>
                            <TableCell>{getTranslation("Rooms",    "Rooms",    "Rooms")}</TableCell>
                            <TableCell>{getTranslation("Reason",   "Reason",   "Reason")}</TableCell>
                            <TableCell align="right">{getTranslation("Actions", "Actions", "Actions")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Box className={classes.loading}>
                                        <CircularProgress size={28} style={{ color: "#0d4f6c" }} />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : blockRules.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Box className={classes.emptyState}>
                                        <DateRangeIcon className={classes.emptyIcon} />
                                        <Typography variant="body2">
                                            {getTranslation(
                                                "No block rules defined yet. Click \"Add Block Rule\" to get started.",
                                                "No block rules defined yet. Click \"Add Block Rule\" to get started.",
                                                "No block rules defined yet. Click \"Add Block Rule\" to get started."
                                            )}
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            blockRules.map((rule) => {
                                const typeConfig = BLOCK_TYPE_CONFIG[rule.blockType] || BLOCK_TYPE_CONFIG["date"];
                                const TypeIcon   = typeConfig.icon || EventIcon;
                                const dtConfig   = DAY_TIME_LABELS[rule.dayTime] || DAY_TIME_LABELS[2];

                                return (
                                    <TableRow key={rule.id} className={classes.tableRow}>
                                        {/* Type */}
                                        <TableCell className={classes.tableCell}>
                                            <Box
                                                className={classes.typeBadge}
                                                style={{
                                                    backgroundColor: typeConfig.color,
                                                    color: typeConfig.text,
                                                }}
                                            >
                                                <TypeIcon style={{ fontSize: 14 }} />
                                                {getTranslation(typeConfig.label, typeConfig.label, typeConfig.label)}
                                            </Box>
                                        </TableCell>

                                        {/* When */}
                                        <TableCell className={classes.tableCell}>
                                            <Typography className={classes.whenText}>
                                                {formatWhen(rule)}
                                            </Typography>
                                            {rule.blockType === "recurring" && (
                                                <Box display="flex" flexWrap="wrap" mt={0.5}>
                                                    {(rule.days || []).map((d) => (
                                                        <Chip
                                                            key={d}
                                                            label={getTranslation(DAY_LABELS[d])}
                                                            size="small"
                                                            className={classes.dayChip}
                                                        />
                                                    ))}
                                                </Box>
                                            )}
                                        </TableCell>

                                        {/* Day Time */}
                                        <TableCell className={classes.tableCell}>
                                            <Chip
                                                label={getTranslation(dtConfig.label, dtConfig.label, dtConfig.label)}
                                                size="small"
                                                className={classes.dayTimeChip}
                                                style={{
                                                    backgroundColor: dtConfig.color,
                                                    color: dtConfig.text,
                                                }}
                                            />
                                        </TableCell>

                                        {/* Rooms */}
                                        <TableCell className={classes.tableCell}>
                                            <Box display="flex" flexWrap="wrap">
                                                {(rule.rooms || []).map((name, i) => (
                                                    <Room id={name}/>
                                                ))}
                                            </Box>
                                        </TableCell>

                                        {/* Reason */}
                                        <TableCell className={classes.tableCell}>
                                            <Typography className={classes.reasonText}>
                                                {rule.reason || "—"}
                                            </Typography>
                                        </TableCell>

                                        {/* Actions */}
                                        <TableCell className={classes.tableCell} align="right">
                                            <Tooltip title={getTranslation("Edit", "Edit", "Edit")}>
                                                <IconButton
                                                    size="small"
                                                    className={classes.editBtn}
                                                    onClick={() => onEdit(rule)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={getTranslation("Delete", "Delete", "Delete")}>
                                                <IconButton
                                                    size="small"
                                                    className={classes.deleteBtn}
                                                    onClick={() => setDeleteTarget(rule)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Delete confirmation */}
            <AlertDialog
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                deleting={isDeleting}
                onSubmit={handleDeleteConfirm}
            />
        </Box>
    );
};

export default BlockRulesTable;

const Room=({id,classes})=>{

const [name, setName] = useState("");

    useEffect(() => {
        Get(
            { id }, 
            "TableManagement/GetArea", 
            null,
            res => {
                setName(res.data.title)
            },
            error => {

            }
        )
    })

    return <Chip
                                                        key={id}
                                                        label={name}
                                                        size="small"
                                                        className={classes.roomChip}
                                                    />
}